import { useState } from 'react';
import type {
  RoutingLoadSettingSource,
  RoutingLoadSettingsResponse,
  UpdateRoutingLoadSettingsRequest,
} from '@nadoutong/contracts';
import {
  useRoutingLoadSettings,
  useUpdateRoutingLoadSettings,
} from '../../api/useRoutingLoad.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, Input, Switch, Textarea } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import {
  LOAD_LAYER_GROUPS,
  TIMEOUT_ENABLE_PREFILL_MS,
  buildKeepOrgIdsPatch,
  buildNumericPatch,
  keepOrgIdsDraftText,
  numericFieldMeta,
} from './loadSettingsFields.ts';
import { SamplingPoliciesSection } from './SamplingPoliciesSection.tsx';
import type {
  BooleanParamField,
  LayerKillSwitchField,
  LayerSwitchField,
  LoadLayerGroup,
  LoadNumericField,
} from './loadSettingsFields.ts';

const SOURCE_TONE: Record<RoutingLoadSettingSource, ChipTone> = {
  custom: 'signal',
  env: 'warn',
  default: 'neutral',
};

const GROUP_TITLE: Record<LoadLayerGroup['id'], I18nKey> = {
  events: 'tenant_routing.load.settings.group.events',
  l2: 'tenant_routing.load.settings.group.l2',
  l3: 'tenant_routing.load.settings.group.l3',
  l4: 'tenant_routing.load.settings.group.l4',
  timeout: 'tenant_routing.load.settings.group.timeout',
};

const KILL_SWITCH_COPY: Record<LayerKillSwitchField, I18nKey> = {
  l2_env_kill_switch: 'tenant_routing.load.settings.kill_switch.l2',
  l3_env_kill_switch: 'tenant_routing.load.settings.kill_switch.l3',
  l4_env_kill_switch: 'tenant_routing.load.settings.kill_switch.l4',
  session_rehome_env_kill_switch: 'tenant_routing.load.settings.kill_switch.session_rehome',
};

function SourceChip({
  source,
  testId,
}: {
  readonly source: RoutingLoadSettingSource;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <Chip tone={SOURCE_TONE[source]} lamp={false} testId={testId}>
      {t(`tenant_routing.load.settings.source.${source}`)}
    </Chip>
  );
}

function NumericFieldRow({
  field,
  data,
  draftValue,
  error,
  saving,
  onDraftChange,
}: {
  readonly field: LoadNumericField;
  readonly data: RoutingLoadSettingsResponse;
  readonly draftValue: string | undefined;
  readonly error: string | null;
  readonly saving: boolean;
  readonly onDraftChange: (value: string) => void;
}): JSX.Element {
  const t = useT();
  const stored = data.settings[field];
  const inputValue = draftValue ?? (stored === null ? '' : String(stored));
  const fieldId = `routing-load-setting-${field}`;
  const meta = numericFieldMeta(field);
  const effective = data.effective[field];
  const placeholder = meta.emptyPlaceholder === 'keep_all'
    ? t('tenant_routing.load.settings.empty.keep_all')
    : meta.emptyPlaceholder === 'off'
      ? t('tenant_routing.load.settings.empty.off')
      : (effective === null ? '' : String(effective));
  return (
    <div
      className="flex flex-col gap-1.5 rounded border border-line bg-card p-3"
      data-testid={`routing-load-setting-${field}`}
    >
      <label htmlFor={fieldId} className="text-label text-paper-mid">
        {t(`tenant_routing.load.settings.field.${field}`)}
      </label>
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t(`tenant_routing.load.settings.field.${field}_desc`)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={fieldId}
          mono
          inputMode={meta.kind === 'decimal' ? 'decimal' : 'numeric'}
          className="max-w-[9rem]"
          placeholder={placeholder}
          value={inputValue}
          invalid={error !== null}
          disabled={saving}
          onChange={(event) => onDraftChange(event.target.value)}
          data-testid={`routing-load-setting-input-${field}`}
        />
        {effective !== null ? (
          <span className="text-dense text-paper-low">
            {t('tenant_routing.load.settings.effective', { value: String(effective) })}
          </span>
        ) : null}
      </div>
      {error !== null ? (
        <p role="alert" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function LayerGroupCard({
  group,
  data,
  draft,
  errors,
  saving,
  advancedOpen,
  onToggleAdvanced,
  onDraftChange,
  onBooleanChange,
  onTimeoutEnable,
  onRestoreLayer,
  onEnableL2,
}: {
  readonly group: LoadLayerGroup;
  readonly data: RoutingLoadSettingsResponse;
  readonly draft: Partial<Record<LoadNumericField, string>>;
  readonly errors: Partial<Record<LoadNumericField, string>>;
  readonly saving: boolean;
  readonly advancedOpen: boolean;
  readonly onToggleAdvanced: () => void;
  readonly onDraftChange: (field: LoadNumericField, value: string) => void;
  readonly onBooleanChange: (field: LayerSwitchField | BooleanParamField, value: boolean) => void;
  readonly onTimeoutEnable: (enabled: boolean) => void;
  readonly onRestoreLayer: (field: LayerSwitchField) => void;
  readonly onEnableL2: () => void;
}): JSX.Element {
  const t = useT();
  const killed = group.killSwitch !== null && data[group.killSwitch];
  const showL3Warning = group.id === 'l3' && data.effective.l3_enabled && !data.effective.l2_enabled;
  const timeoutOn = data.effective.attempt_first_byte_timeout_ms !== null;
  const visibleNumerics = group.id === 'timeout' && !timeoutOn ? [] : group.visibleNumerics;
  const switchField = group.switchField;

  return (
    <div
      className="flex flex-col gap-3 rounded border border-line bg-raised p-3"
      data-testid={`routing-load-group-${group.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-label text-paper">{t(GROUP_TITLE[group.id])}</h3>
        {switchField !== null ? (
          <span className="inline-flex items-center gap-1.5">
            <SourceChip
              source={data.sources[switchField]}
              testId={`routing-load-setting-source-${switchField}`}
            />
            {data.sources[switchField] === 'custom' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => onRestoreLayer(switchField)}
                data-testid={`routing-load-setting-restore-${switchField}`}
              >
                {t('tenant_routing.load.settings.restore')}
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>

      {killed && group.killSwitch !== null ? (
        <p
          className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-5 text-warn"
          data-testid={`routing-load-settings-kill-${group.id}`}
        >
          {t(KILL_SWITCH_COPY[group.killSwitch])}
        </p>
      ) : null}

      {switchField !== null ? (
        <div className="flex flex-col gap-1.5">
          <Switch
            checked={data.effective[switchField]}
            onCheckedChange={(next) => onBooleanChange(switchField, next)}
            disabled={saving || killed}
            label={t(`tenant_routing.load.settings.field.${switchField}`)}
            testId={`routing-load-setting-switch-${switchField}`}
          />
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t(`tenant_routing.load.settings.field.${switchField}_desc`)}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Switch
            checked={timeoutOn}
            onCheckedChange={onTimeoutEnable}
            disabled={saving}
            label={t('tenant_routing.load.settings.timeout_enable')}
            testId="routing-load-timeout-enable-switch"
          />
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t('tenant_routing.load.settings.timeout_enable_desc')}
          </p>
        </div>
      )}

      {showL3Warning ? (
        <div
          className="flex flex-col gap-2 rounded border border-warn-line bg-warn-dim/50 px-3 py-2"
          data-testid="routing-load-settings-l3-without-l2"
        >
          <p className="m-0 text-dense leading-5 text-warn">
            {t('tenant_routing.load.settings.warning.l3_without_l2')}
          </p>
          <Button
            size="sm"
            disabled={saving || data.l2_env_kill_switch}
            onClick={onEnableL2}
            data-testid="routing-load-settings-enable-l2"
          >
            {t('tenant_routing.load.settings.enable_l2_with_l3')}
          </Button>
        </div>
      ) : null}

      {group.visibleBooleans.map((field) => (
        <div key={field} className="flex flex-col gap-1.5" data-testid={`routing-load-setting-${field}`}>
          <Switch
            checked={data.effective[field]}
            onCheckedChange={(next) => onBooleanChange(field, next)}
            disabled={saving}
            label={t(`tenant_routing.load.settings.field.${field}`)}
            testId={`routing-load-setting-switch-${field}`}
          />
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t(`tenant_routing.load.settings.field.${field}_desc`)}
          </p>
        </div>
      ))}

      {visibleNumerics.map((field) => (
        <NumericFieldRow
          key={field}
          field={field}
          data={data}
          draftValue={draft[field]}
          error={errors[field] ?? null}
          saving={saving}
          onDraftChange={(value) => onDraftChange(field, value)}
        />
      ))}

      {group.advancedNumerics.length > 0 ? (
        <div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleAdvanced}
            data-testid={`routing-load-advanced-toggle-${group.id}`}
          >
            {t('tenant_routing.load.settings.advanced')}
          </Button>
          {advancedOpen ? (
            <div className="mt-2 flex flex-col gap-2" data-testid={`routing-load-advanced-${group.id}`}>
              {group.advancedNumerics.map((field) => (
                <NumericFieldRow
                  key={field}
                  field={field}
                  data={data}
                  draftValue={draft[field]}
                  error={errors[field] ?? null}
                  saving={saving}
                  onDraftChange={(value) => onDraftChange(field, value)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function RoutingLoadSettingsPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const settings = useRoutingLoadSettings();
  const updateSettings = useUpdateRoutingLoadSettings();
  const [draft, setDraft] = useState<Partial<Record<LoadNumericField, string>>>({});
  const [orgIdsDraft, setOrgIdsDraft] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<Partial<Record<LoadNumericField, string>>>({});
  const [advancedOpen, setAdvancedOpen] = useState<Partial<Record<LoadLayerGroup['id'], boolean>>>({});

  const data = settings.data;
  if (settings.isPending) {
    return (
      <p className="m-0 text-body text-paper-low" data-testid="routing-load-settings-loading">
        {t('tenant_routing.load.settings.loading')}
      </p>
    );
  }
  if (data === undefined) {
    return (
      <p
        className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
        data-testid="routing-load-settings-load-failed"
      >
        {t('tenant_routing.load.settings.load_failed')}
      </p>
    );
  }

  const saving = updateSettings.isPending;

  function mutatePatch(patch: UpdateRoutingLoadSettingsRequest, successMessage: string): void {
    updateSettings.mutate(patch, {
      onSuccess: () => toast.success(successMessage),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  function handleBooleanChange(field: LayerSwitchField | BooleanParamField, value: boolean): void {
    mutatePatch({ [field]: value }, t('tenant_routing.load.settings.toast.saved'));
  }

  function handleTimeoutEnable(next: boolean): void {
    mutatePatch(
      { attempt_first_byte_timeout_ms: next ? TIMEOUT_ENABLE_PREFILL_MS : null },
      t('tenant_routing.load.settings.toast.saved'),
    );
  }

  function handleSave(): void {
    if (data === undefined) return;
    const { patch, errors: invalid } = buildNumericPatch(draft, data.settings);
    const orgIds = buildKeepOrgIdsPatch(orgIdsDraft, data.settings.lifecycle_keep_org_ids);
    const nextErrors: Partial<Record<LoadNumericField, string>> = {};
    for (const field of invalid) {
      nextErrors[field] = numericFieldMeta(field).kind === 'int'
        ? t('tenant_routing.load.settings.invalid_int')
        : t('tenant_routing.load.settings.invalid_decimal');
    }
    setErrors(nextErrors);
    if (invalid.length > 0) return;
    const body: UpdateRoutingLoadSettingsRequest = orgIds.unchanged
      ? patch
      : { ...patch, lifecycle_keep_org_ids: orgIds.value };
    if (Object.keys(body).length === 0) {
      setDraft({});
      setOrgIdsDraft(undefined);
      return;
    }
    updateSettings.mutate(body, {
      onSuccess: () => {
        setDraft({});
        setOrgIdsDraft(undefined);
        toast.success(t('tenant_routing.load.settings.toast.saved'));
      },
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="routing-load-settings-panel">
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t('tenant_routing.load.settings.hint')}
      </p>
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t('tenant_routing.load.settings.order_hint')}
      </p>

      {LOAD_LAYER_GROUPS.map((group) => (
        <LayerGroupCard
          key={group.id}
          group={group}
          data={data}
          draft={draft}
          errors={errors}
          saving={saving}
          advancedOpen={advancedOpen[group.id] === true}
          onToggleAdvanced={() => {
            setAdvancedOpen((prev) => ({ ...prev, [group.id]: prev[group.id] !== true }));
          }}
          onDraftChange={(field, value) => {
            setDraft((prev) => ({ ...prev, [field]: value }));
          }}
          onBooleanChange={handleBooleanChange}
          onTimeoutEnable={handleTimeoutEnable}
          onRestoreLayer={(field) => {
            mutatePatch({ [field]: null }, t('tenant_routing.load.settings.toast.restored'));
          }}
          onEnableL2={() => {
            mutatePatch({ l2_enabled: true }, t('tenant_routing.load.settings.toast.saved'));
          }}
        />
      ))}

      <div
        className="flex flex-col gap-3 rounded border border-line bg-raised p-3"
        data-testid="routing-load-group-lifecycle-sampling"
      >
        <h3 className="m-0 text-label text-paper">
          {t('tenant_routing.load.settings.group.lifecycle_sampling')}
        </h3>
        <p className="m-0 text-dense leading-5 text-paper-low">
          {t('tenant_routing.load.settings.group.lifecycle_sampling_hint')}
        </p>
        <p
          className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-5 text-warn"
          data-testid="routing-load-lifecycle-superseded-hint"
        >
          {t('tenant_routing.load.settings.group.lifecycle_sampling_superseded')}
        </p>
        {(['lifecycle_sample_p_jp', 'lifecycle_sample_p_cn', 'lifecycle_keep_slow_ms'] as const).map((field) => (
          <NumericFieldRow
            key={field}
            field={field}
            data={data}
            draftValue={draft[field]}
            error={errors[field] ?? null}
            saving={saving}
            onDraftChange={(value) => {
              setDraft((prev) => ({ ...prev, [field]: value }));
            }}
          />
        ))}
        <div
          className="flex flex-col gap-1.5 rounded border border-line bg-card p-3"
          data-testid="routing-load-setting-lifecycle_keep_org_ids"
        >
          <label htmlFor="routing-load-setting-lifecycle_keep_org_ids" className="text-label text-paper-mid">
            {t('tenant_routing.load.settings.field.lifecycle_keep_org_ids')}
          </label>
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t('tenant_routing.load.settings.field.lifecycle_keep_org_ids_desc')}
          </p>
          <Textarea
            id="routing-load-setting-lifecycle_keep_org_ids"
            mono
            rows={3}
            value={orgIdsDraft ?? keepOrgIdsDraftText(data.settings.lifecycle_keep_org_ids)}
            disabled={saving}
            onChange={(event) => setOrgIdsDraft(event.target.value)}
            data-testid="routing-load-setting-input-lifecycle_keep_org_ids"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="m-0 text-label text-paper-low">
          {t('tenant_routing.load.settings.save_hint')}
        </p>
        <Button
          variant="primary"
          loading={saving}
          onClick={handleSave}
          data-testid="routing-load-settings-save"
        >
          {t('tenant_routing.load.settings.save')}
        </Button>
      </div>

      <SamplingPoliciesSection />
    </div>
  );
}
